// src/scripts/sqlApiHelper/stockCountApi.js

import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";

import {
  getFunctions,
  httpsCallable,
} from "firebase/functions";

import {
  auth,
  db,
} from "@/firebase";

import {
  fetchStockUnits,
} from "@/scripts/sqlApiHelper/stockUnitsApi";

const functions =
  getFunctions(
    undefined,
    "us-central1"
  );

const saveApprovedDeliveryNoteCallable =
  httpsCallable(
    functions,
    "saveApprovedDeliveryNote"
  );

function cleanString(value) {
  return String(
    value ?? ""
  ).trim();
}

function cleanNumber(value) {
  const n =
    Number(value);

  return Number.isFinite(
    n
  )
    ? n
    : 0;
}

function positiveNumber(value) {
  return Math.abs(
    cleanNumber(value)
  );
}

function uniqueStrings(values) {
  return Array.from(
    new Set(
      (
        Array.isArray(values)
          ? values
          : []
      )
        .map(cleanString)
        .filter(Boolean)
    )
  );
}

function normaliseRecipeSalesEntries(
  entries
) {
  return (
    Array.isArray(entries)
      ? entries
      : []
  )
    .map(
      (entry) => ({
        recipe_id:
          cleanString(
            entry.recipe_id
          ),

        recipe_name:
          cleanString(
            entry.recipe_name
          ),

        quantity_sold:
          cleanNumber(
            entry.quantity_sold
          ),
      })
    )
    .filter(
      (entry) =>
        entry.recipe_id &&
        entry.quantity_sold >
          0
    );
}

function timestampMillis(value) {
  if (!value) {
    return 0;
  }

  if (
    typeof value.toMillis ===
    "function"
  ) {
    return value.toMillis();
  }

  if (
    typeof value.toDate ===
    "function"
  ) {
    return value
      .toDate()
      .getTime();
  }

  if (
    Number.isFinite(
      Number(
        value.seconds
      )
    )
  ) {
    return (
      Number(
        value.seconds
      ) * 1000
    );
  }

  const parsed =
    new Date(value);

  return Number.isNaN(
    parsed.getTime()
  )
    ? 0
    : parsed.getTime();
}

/* =========================================================
   STOCK PRODUCT DATABASE
   ========================================================= */

export async function fetchStockProductsForCount() {
  /*
   * Use stockUnitsApi rather than reading raw Firestore directly
   * so the count receives the explicit new conversion model and
   * legacy normalisation in one place.
   */
  return fetchStockUnits();
}

export function subscribeStockProductsForCount(
  onChange,
  onError
) {
  return onSnapshot(
    collection(
      db,
      "stock_units"
    ),

    async () => {
      try {
        const products =
          await fetchStockUnits();

        if (
          typeof onChange ===
          "function"
        ) {
          onChange(
            products
          );
        }
      } catch (error) {
        if (
          typeof onError ===
          "function"
        ) {
          onError(
            error
          );
        }
      }
    },

    (error) => {
      if (
        typeof onError ===
        "function"
      ) {
        onError(
          error
        );
      }
    }
  );
}

/* =========================================================
   ACTIVE APPROVED ORDERS
   ========================================================= */

export async function fetchActiveDeliveryOrders(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    return [];
  }

  const snapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "active_delivery_orders"
      )
    );

  return snapshot.docs
    .map(
      (orderDocument) => ({
        id:
          orderDocument.id,

        ...orderDocument.data(),
      })
    )
    .filter(
      (order) =>
        order.status ===
        "awaiting_delivery"
    )
    .sort(
      (a, b) => {
        const aTime =
          typeof a.approvedAt
            ?.toMillis ===
          "function"
            ? a.approvedAt.toMillis()
            : 0;

        const bTime =
          typeof b.approvedAt
            ?.toMillis ===
          "function"
            ? b.approvedAt.toMillis()
            : 0;

        return (
          aTime -
          bTime
        );
      }
    );
}

/* =========================================================
   SAVE APPROVED DELIVERY NOTE

   quantityDelivered is ORDER UNITS.

   The existing backend stores:
   - quantity_delivered
   - stock_units_per_unit

   StockCountView multiplies those values to produce Stock In.
   ========================================================= */

export async function saveApprovedStockDeliveryNote({
  venueId,
  orderId,
  deliveryDate,
  deliveries,
}) {
  const venue =
    cleanString(
      venueId
    );

  const order =
    cleanString(
      orderId
    );

  const date =
    cleanString(
      deliveryDate
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  if (!order) {
    throw new Error(
      "Select an approved order."
    );
  }

  if (
    !/^\d{4}-\d{2}-\d{2}$/.test(
      date
    )
  ) {
    throw new Error(
      "A valid delivery date is required."
    );
  }

  /*
   * IMPORTANT:
   *
   * The callable backend reads data.items / data.deliveredItems.
   *
   * It does NOT read data.deliveries.
   */
  const items =
    (
      Array.isArray(
        deliveries
      )
        ? deliveries
        : []
    ).map(
      (line) => ({
        id:
          cleanString(
            line.itemId ||
            line.product_id
          ),

        quantityDelivered:
          cleanNumber(
            line.quantityDelivered ??
            line.quantity_delivered
          ),
      })
    );

  const result =
    await saveApprovedDeliveryNoteCallable(
      {
        venueId:
          venue,

        orderId:
          order,

        deliveryDate:
          date,

        items,
      }
    );

  return (
    result?.data ||
    {}
  );
}

/* =========================================================
   LEGACY / MANUAL DELIVERY NOTE SAVE
   ========================================================= */

export async function saveStockDeliveryNote({
  venueId,
  deliveryDate,
  deliveryNoteNumber,
  lines,
}) {
  const venue =
    cleanString(
      venueId
    );

  const date =
    cleanString(
      deliveryDate
    );

  const noteNumber =
    cleanString(
      deliveryNoteNumber
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  if (!date) {
    throw new Error(
      "Delivery date is required."
    );
  }

  if (!noteNumber) {
    throw new Error(
      "Delivery note number is required."
    );
  }

  if (
    !Array.isArray(lines) ||
    !lines.length
  ) {
    throw new Error(
      "The delivery note has no product lines."
    );
  }

  const user =
    auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to save a delivery note."
    );
  }

  let totalQuantity =
    0;

  let totalValue =
    0;

  for (
    const line of lines
  ) {
    const quantity =
      cleanNumber(
        line.quantity_delivered
      );

    const unitCost =
      cleanNumber(
        line
          .unit_cost_order_unit ??
        line.unit_cost
      );

    if (
      !cleanString(
        line.product_id
      )
    ) {
      throw new Error(
        "A delivery line is missing its Stock Database product."
      );
    }

    if (
      quantity <= 0
    ) {
      throw new Error(
        "Delivery quantities must be greater than zero."
      );
    }

    totalQuantity +=
      quantity;

    totalValue +=
      quantity *
      unitCost;
  }

  const deliveryNoteReference =
    doc(
      collection(
        db,
        "venues",
        venue,
        "stock_delivery_notes"
      )
    );

  await setDoc(
    deliveryNoteReference,
    {
      venue_id:
        venue,

      delivery_date:
        date,

      delivery_note_number:
        noteNumber,

      line_count:
        lines.length,

      total_quantity:
        totalQuantity,

      total_value:
        totalValue,

      included_in_count_id:
        null,

      included_in_count_at:
        null,

      status:
        "saving",

      created_by_uid:
        user.uid,

      created_by_email:
        user.email ||
        "",

      created_at:
        serverTimestamp(),

      updated_at:
        serverTimestamp(),
    }
  );

  const BATCH_SIZE =
    400;

  for (
    let start = 0;
    start <
    lines.length;
    start +=
      BATCH_SIZE
  ) {
    const batch =
      writeBatch(db);

    const chunk =
      lines.slice(
        start,
        start +
          BATCH_SIZE
      );

    for (
      const line of
      chunk
    ) {
      const quantityOrderUnits =
        cleanNumber(
          line.quantity_delivered
        );

      const stockPerOrder =
        cleanNumber(
          line
            .stock_units_per_order_unit ??
          line
            .stock_units_per_unit
        );

      const quantityStockUnits =
        quantityOrderUnits *
        stockPerOrder;

      const orderUnitCost =
        cleanNumber(
          line
            .unit_cost_order_unit ??
          line.unit_cost
        );

      const stockUnitCost =
        stockPerOrder > 0
          ? orderUnitCost /
            stockPerOrder
          : 0;

      const lineReference =
        doc(
          collection(
            deliveryNoteReference,
            "lines"
          )
        );

      batch.set(
        lineReference,
        {
          product_key:
            cleanString(
              line.product_key
            ),

          product_id:
            cleanString(
              line.product_id
            ),

          product_name:
            cleanString(
              line.product_name
            ),

          product_group:
            cleanString(
              line.product_group
            ),

          supplier:
            cleanString(
              line.supplier
            ),

          order_unit:
            cleanString(
              line.order_unit
            ),

          stock_unit:
            cleanString(
              line.stock_unit
            ),

          stock_units_per_order_unit:
            stockPerOrder,

          sales_units_per_stock_unit:
            cleanNumber(
              line
                .sales_units_per_stock_unit
            ),

          unit_cost_order_unit:
            orderUnitCost,

          unit_cost_stock_unit:
            stockUnitCost,

          /*
           * Compatibility.
           */
          unit_size:
            cleanString(
              line.stock_unit ||
              line.unit_size
            ),

          unit_cost:
            orderUnitCost,

          stock_units_per_unit:
            stockPerOrder,

          /*
           * Keep quantity_delivered as ORDER UNITS for the
           * existing delivery-note convention.
           */
          quantity_delivered:
            quantityOrderUnits,

          quantity_delivered_stock_units:
            quantityStockUnits,

          line_value:
            quantityOrderUnits *
            orderUnitCost,

          created_at:
            serverTimestamp(),

          updated_at:
            serverTimestamp(),
        }
      );
    }

    await batch.commit();
  }

  await updateDoc(
    deliveryNoteReference,
    {
      status:
        "complete",

      completed_at:
        serverTimestamp(),

      updated_at:
        serverTimestamp(),
    }
  );

  return (
    deliveryNoteReference.id
  );
}

/* =========================================================
   FETCH DELIVERY NOTES
   ========================================================= */

export async function fetchStockDeliveryNotes(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    return [];
  }

  const snapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "stock_delivery_notes"
      )
    );

  const notes =
    await Promise.all(
      snapshot.docs.map(
        async (
          deliveryDocument
        ) => {
          const linesSnapshot =
            await getDocs(
              collection(
                db,
                "venues",
                venue,
                "stock_delivery_notes",
                deliveryDocument.id,
                "lines"
              )
            );

          return {
            id:
              deliveryDocument.id,

            ...deliveryDocument.data(),

            lines:
              linesSnapshot.docs.map(
                (lineDocument) => ({
                  id:
                    lineDocument.id,

                  ...lineDocument.data(),
                })
              ),
          };
        }
      )
    );

  notes.sort(
    (a, b) =>
      cleanString(
        b.delivery_date
      ).localeCompare(
        cleanString(
          a.delivery_date
        )
      )
  );

  return notes;
}

export async function fetchPendingStockDeliveryNotes(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  const snapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "stock_delivery_notes"
      )
    );

  const pendingDocuments =
    snapshot.docs.filter(
      (deliveryDocument) => {
        const data =
          deliveryDocument.data();

        return (
          cleanString(
            data?.status
          ) ===
            "complete" &&
          !cleanString(
            data
              ?.included_in_count_id
          )
        );
      }
    );

  return Promise.all(
    pendingDocuments.map(
      async (
        deliveryDocument
      ) => {
        const linesSnapshot =
          await getDocs(
            collection(
              db,
              "venues",
              venue,
              "stock_delivery_notes",
              deliveryDocument.id,
              "lines"
            )
          );

        return {
          id:
            deliveryDocument.id,

          ...deliveryDocument.data(),

          lines:
            linesSnapshot.docs.map(
              (lineDocument) => ({
                id:
                  lineDocument.id,

                ...lineDocument.data(),
              })
            ),
        };
      }
    )
  );
}

/* =========================================================
   INTERNAL TRANSFERS
   ========================================================= */

export async function saveStockInternalTransfer({
  transferDate,
  transferReference,
  fromVenueId,
  fromVenueName,
  toVenueId,
  toVenueName,
  lines,
}) {
  const fromVenue =
    cleanString(
      fromVenueId
    );

  const toVenue =
    cleanString(
      toVenueId
    );

  const date =
    cleanString(
      transferDate
    );

  const reference =
    cleanString(
      transferReference
    );

  if (
    !fromVenue ||
    !toVenue
  ) {
    throw new Error(
      "Venue From and Venue To are required."
    );
  }

  if (
    fromVenue ===
    toVenue
  ) {
    throw new Error(
      "Venue From and Venue To cannot be the same."
    );
  }

  if (
    !date ||
    !reference
  ) {
    throw new Error(
      "Transfer date and reference are required."
    );
  }

  if (
    !Array.isArray(lines) ||
    !lines.length
  ) {
    throw new Error(
      "The transfer has no product lines."
    );
  }

  const user =
    auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to save an internal transfer."
    );
  }

  let totalQuantity =
    0;

  let totalValue =
    0;

  for (
    const line of lines
  ) {
    const quantity =
      positiveNumber(
        line
          .quantity_transferred
      );

    const stockPerOrder =
      cleanNumber(
        line
          .stock_units_per_order_unit ??
        line
          .stock_units_per_unit
      );

    const orderUnitCost =
      cleanNumber(
        line
          .unit_cost_order_unit ??
        line.unit_cost
      );

    const stockUnitCost =
      stockPerOrder > 0
        ? orderUnitCost /
          stockPerOrder
        : orderUnitCost;

    totalQuantity +=
      quantity;

    totalValue +=
      quantity *
      stockUnitCost;
  }

  const transferId =
    doc(
      collection(
        db,
        "venues",
        fromVenue,
        "stock_internal_transfers"
      )
    ).id;

  const fromReference =
    doc(
      db,
      "venues",
      fromVenue,
      "stock_internal_transfers",
      transferId
    );

  const toReference =
    doc(
      db,
      "venues",
      toVenue,
      "stock_internal_transfers",
      transferId
    );

  const commonHeader = {
    transfer_id:
      transferId,

    transfer_date:
      date,

    transfer_reference:
      reference,

    from_venue_id:
      fromVenue,

    from_venue_name:
      cleanString(
        fromVenueName
      ),

    to_venue_id:
      toVenue,

    to_venue_name:
      cleanString(
        toVenueName
      ),

    line_count:
      lines.length,

    total_quantity:
      totalQuantity,

    total_value:
      totalValue,

    included_in_count_id:
      null,

    included_in_count_at:
      null,

    status:
      "saving",

    created_by_uid:
      user.uid,

    created_by_email:
      user.email ||
      "",

    created_at:
      serverTimestamp(),

    updated_at:
      serverTimestamp(),
  };

  const headerBatch =
    writeBatch(db);

  headerBatch.set(
    fromReference,
    {
      ...commonHeader,

      venue_id:
        fromVenue,

      direction:
        "out",

      signed_total_quantity:
        -totalQuantity,
    }
  );

  headerBatch.set(
    toReference,
    {
      ...commonHeader,

      venue_id:
        toVenue,

      direction:
        "in",

      signed_total_quantity:
        totalQuantity,
    }
  );

  await headerBatch.commit();

  const BATCH_SIZE =
    200;

  for (
    let start = 0;
    start <
    lines.length;
    start +=
      BATCH_SIZE
  ) {
    const batch =
      writeBatch(db);

    const chunk =
      lines.slice(
        start,
        start +
          BATCH_SIZE
      );

    for (
      const line of
      chunk
    ) {
      const quantity =
        positiveNumber(
          line
            .quantity_transferred
        );

      const stockPerOrder =
        cleanNumber(
          line
            .stock_units_per_order_unit ??
          line
            .stock_units_per_unit
        );

      const orderUnitCost =
        cleanNumber(
          line
            .unit_cost_order_unit ??
          line.unit_cost
        );

      const stockUnitCost =
        stockPerOrder > 0
          ? orderUnitCost /
            stockPerOrder
          : orderUnitCost;

      const lineId =
        doc(
          collection(
            db,
            "venues",
            fromVenue,
            "stock_internal_transfers",
            transferId,
            "lines"
          )
        ).id;

      const commonLine = {
        product_key:
          cleanString(
            line.product_key
          ),

        product_id:
          cleanString(
            line.product_id
          ),

        product_name:
          cleanString(
            line.product_name
          ),

        product_group:
          cleanString(
            line.product_group
          ),

        supplier:
          cleanString(
            line.supplier
          ),

        order_unit:
          cleanString(
            line.order_unit
          ),

        stock_unit:
          cleanString(
            line.stock_unit ||
            line.unit_size
          ),

        stock_units_per_order_unit:
          stockPerOrder,

        sales_units_per_stock_unit:
          cleanNumber(
            line
              .sales_units_per_stock_unit
          ),

        unit_cost_order_unit:
          orderUnitCost,

        unit_cost_stock_unit:
          stockUnitCost,

        unit_size:
          cleanString(
            line.stock_unit ||
            line.unit_size
          ),

        unit_cost:
          orderUnitCost,

        stock_units_per_unit:
          stockPerOrder,

        quantity_transferred:
          quantity,

        line_value:
          quantity *
          stockUnitCost,

        created_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),
      };

      batch.set(
        doc(
          db,
          "venues",
          fromVenue,
          "stock_internal_transfers",
          transferId,
          "lines",
          lineId
        ),
        {
          ...commonLine,

          direction:
            "out",

          signed_quantity:
            -quantity,
        }
      );

      batch.set(
        doc(
          db,
          "venues",
          toVenue,
          "stock_internal_transfers",
          transferId,
          "lines",
          lineId
        ),
        {
          ...commonLine,

          direction:
            "in",

          signed_quantity:
            quantity,
        }
      );
    }

    await batch.commit();
  }

  const completeBatch =
    writeBatch(db);

  completeBatch.update(
    fromReference,
    {
      status:
        "complete",

      completed_at:
        serverTimestamp(),

      updated_at:
        serverTimestamp(),
    }
  );

  completeBatch.update(
    toReference,
    {
      status:
        "complete",

      completed_at:
        serverTimestamp(),

      updated_at:
        serverTimestamp(),
    }
  );

  await completeBatch.commit();

  return transferId;
}

export async function fetchStockInternalTransfers(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    return [];
  }

  const snapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "stock_internal_transfers"
      )
    );

  const transfers =
    await Promise.all(
      snapshot.docs.map(
        async (
          transferDocument
        ) => {
          const linesSnapshot =
            await getDocs(
              collection(
                db,
                "venues",
                venue,
                "stock_internal_transfers",
                transferDocument.id,
                "lines"
              )
            );

          return {
            id:
              transferDocument.id,

            ...transferDocument.data(),

            lines:
              linesSnapshot.docs.map(
                (lineDocument) => ({
                  id:
                    lineDocument.id,

                  ...lineDocument.data(),
                })
              ),
          };
        }
      )
    );

  transfers.sort(
    (a, b) =>
      cleanString(
        b.transfer_date
      ).localeCompare(
        cleanString(
          a.transfer_date
        )
      )
  );

  return transfers;
}

export async function fetchPendingStockInternalTransfers(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  const snapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "stock_internal_transfers"
      )
    );

  const pendingDocuments =
    snapshot.docs.filter(
      (transferDocument) => {
        const data =
          transferDocument.data();

        return (
          cleanString(
            data?.status
          ) ===
            "complete" &&
          !cleanString(
            data
              ?.included_in_count_id
          )
        );
      }
    );

  return Promise.all(
    pendingDocuments.map(
      async (
        transferDocument
      ) => {
        const linesSnapshot =
          await getDocs(
            collection(
              db,
              "venues",
              venue,
              "stock_internal_transfers",
              transferDocument.id,
              "lines"
            )
          );

        return {
          id:
            transferDocument.id,

          ...transferDocument.data(),

          lines:
            linesSnapshot.docs.map(
              (lineDocument) => ({
                id:
                  lineDocument.id,

                ...lineDocument.data(),
              })
            ),
        };
      }
    )
  );
}

/* =========================================================
   STOCK COUNTS
   ========================================================= */

async function loadStockCountDocument(
  venue,
  countDocument
) {
  const linesSnapshot =
    await getDocs(
      collection(
        db,
        "venues",
        venue,
        "stock_counts",
        countDocument.id,
        "lines"
      )
    );

  return {
    id:
      countDocument.id,

    ...countDocument.data(),

    lines:
      linesSnapshot.docs.map(
        (lineDocument) => ({
          id:
            lineDocument.id,

          ...lineDocument.data(),
        })
      ),
  };
}

export async function fetchLatestStockCount(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  const countsQuery =
    query(
      collection(
        db,
        "venues",
        venue,
        "stock_counts"
      ),

      orderBy(
        "completed_at",
        "desc"
      ),

      limit(1)
    );

  const snapshot =
    await getDocs(
      countsQuery
    );

  if (
    snapshot.empty
  ) {
    return null;
  }

  return loadStockCountDocument(
    venue,
    snapshot.docs[0]
  );
}

export async function fetchDraftStockCount(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    return null;
  }

  const draftQuery =
    query(
      collection(
        db,
        "venues",
        venue,
        "stock_counts"
      ),

      where(
        "status",
        "==",
        "draft"
      )
    );

  const snapshot =
    await getDocs(
      draftQuery
    );

  if (
    snapshot.empty
  ) {
    return null;
  }

  const drafts = [
    ...snapshot.docs,
  ];

  drafts.sort(
    (a, b) => {
      const aData =
        a.data();

      const bData =
        b.data();

      return (
        timestampMillis(
          bData.updated_at ||
          bData.saved_at ||
          bData.created_at
        ) -
        timestampMillis(
          aData.updated_at ||
          aData.saved_at ||
          aData.created_at
        )
      );
    }
  );

  return loadStockCountDocument(
    venue,
    drafts[0]
  );
}

export async function fetchCompletedStockCountsLast13Weeks(
  venueId
) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    return [];
  }

  const cutoffDate =
    new Date();

  cutoffDate.setDate(
    cutoffDate.getDate() -
    13 * 7
  );

  cutoffDate.setHours(
    0,
    0,
    0,
    0
  );

  const cutoffTimestamp =
    Timestamp.fromDate(
      cutoffDate
    );

  const countsQuery =
    query(
      collection(
        db,
        "venues",
        venue,
        "stock_counts"
      ),

      where(
        "completed_at",
        ">=",
        cutoffTimestamp
      ),

      orderBy(
        "completed_at",
        "desc"
      )
    );

  const snapshot =
    await getDocs(
      countsQuery
    );

  return Promise.all(
    snapshot.docs.map(
      (countDocument) =>
        loadStockCountDocument(
          venue,
          countDocument
        )
    )
  );
}

function stockCountLineData(
  line
) {
  const orderUnitCost =
    cleanNumber(
      line
        .unit_cost_order_unit ??
      line.unit_cost
    );

  const stockPerOrder =
    cleanNumber(
      line
        .stock_units_per_order_unit ??
      line
        .stock_units_per_unit
    );

  const stockUnitCost =
    cleanNumber(
      line
        .unit_cost_stock_unit
    ) ||
    (
      stockPerOrder > 0
        ? orderUnitCost /
          stockPerOrder
        : 0
    );

  return {
    product_key:
      cleanString(
        line.product_key
      ),

    product_id:
      cleanString(
        line.product_id
      ),

    product_name:
      cleanString(
        line.product_name
      ),

    product_group:
      cleanString(
        line.product_group
      ),

    supplier:
      cleanString(
        line.supplier
      ),

    order_unit:
      cleanString(
        line.order_unit
      ),

    stock_unit:
      cleanString(
        line.stock_unit
      ),

    stock_units_per_order_unit:
      stockPerOrder,

    sales_units_per_stock_unit:
      cleanNumber(
        line
          .sales_units_per_stock_unit
      ),

    unit_cost_order_unit:
      orderUnitCost,

    unit_cost_stock_unit:
      stockUnitCost,

    /*
     * Legacy/report compatibility.
     */
    unit_size:
      cleanString(
        line.stock_unit ||
        line.unit_size
      ),

    unit_cost:
      orderUnitCost,

    stock_units_per_unit:
      stockPerOrder,

    opening_count:
      cleanNumber(
        line.opening_count
      ),

    stock_in:
      cleanNumber(
        line.stock_in
      ),

    stock_in_from_deliveries:
      cleanNumber(
        line
          .stock_in_from_deliveries
      ),

    stock_in_from_transfers:
      cleanNumber(
        line
          .stock_in_from_transfers
      ),

    stock_out:
      cleanNumber(
        line.stock_out
      ),

    stock_out_from_transfers:
      cleanNumber(
        line
          .stock_out_from_transfers
      ),

    closing_count:
      cleanNumber(
        line.closing_count
      ),

    sales:
      cleanNumber(
        line.sales
      ),

    recipe_sales:
      cleanNumber(
        line.recipe_sales
      ),

    total_sales:
      cleanNumber(
        line.total_sales
      ),

    sales_stock_units:
      cleanNumber(
        line.sales_stock_units
      ),

    available_stock:
      cleanNumber(
        line.available_stock
      ),

    consumption:
      cleanNumber(
        line.consumption
      ),

    variance_units:
      cleanNumber(
        line.variance_units
      ),

    variance_value:
      cleanNumber(
        line.variance_value
      ),

    updated_at:
      serverTimestamp(),
  };
}

async function replaceStockCountLines(
  countReference,
  lines
) {
  const linesCollection =
    collection(
      countReference,
      "lines"
    );

  const existingSnapshot =
    await getDocs(
      linesCollection
    );

  const BATCH_SIZE =
    400;

  for (
    let start = 0;
    start <
    existingSnapshot.docs
      .length;
    start +=
      BATCH_SIZE
  ) {
    const batch =
      writeBatch(db);

    for (
      const existingDocument of
      existingSnapshot.docs.slice(
        start,
        start +
          BATCH_SIZE
      )
    ) {
      batch.delete(
        existingDocument.ref
      );
    }

    await batch.commit();
  }

  for (
    let start = 0;
    start <
    lines.length;
    start +=
      BATCH_SIZE
  ) {
    const batch =
      writeBatch(db);

    for (
      const line of
      lines.slice(
        start,
        start +
          BATCH_SIZE
      )
    ) {
      const lineReference =
        doc(
          linesCollection
        );

      batch.set(
        lineReference,
        {
          ...stockCountLineData(
            line
          ),

          created_at:
            serverTimestamp(),
        }
      );
    }

    await batch.commit();
  }
}

export async function saveStockCountDraft({
  venueId,
  countId = "",
  lines,
  deliveryNoteIds = [],
  internalTransferIds = [],
  recipeSalesEntries = [],
}) {
  const venue =
    cleanString(
      venueId
    );

  if (!venue) {
    throw new Error(
      "Missing venue ID."
    );
  }

  if (
    !Array.isArray(lines) ||
    !lines.length
  ) {
    throw new Error(
      "There are no stock-count lines to save."
    );
  }

  const user =
    auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to save a stock count."
    );
  }

  for (
    const line of lines
  ) {
    if (
      !cleanString(
        line.product_key
      ) ||
      !cleanString(
        line.product_id
      )
    ) {
      throw new Error(
        "A stock-count line is missing its product record."
      );
    }

    if (
      cleanNumber(
        line
          .stock_units_per_order_unit
      ) <= 0 ||
      cleanNumber(
        line
          .sales_units_per_stock_unit
      ) <= 0
    ) {
      throw new Error(
        `${cleanString(
          line.product_name
        ) || "A product"} does not have complete Stock Database conversion values.`
      );
    }
  }

  const deliveryIds =
    uniqueStrings(
      deliveryNoteIds
    );

  const transferIds =
    uniqueStrings(
      internalTransferIds
    );

  const cleanedRecipeSalesEntries =
    normaliseRecipeSalesEntries(
      recipeSalesEntries
    );

  let countReference;
  let existing = false;

  if (
    cleanString(
      countId
    )
  ) {
    countReference =
      doc(
        db,
        "venues",
        venue,
        "stock_counts",
        cleanString(
          countId
        )
      );

    const existingSnapshot =
      await getDoc(
        countReference
      );

    if (
      existingSnapshot.exists()
    ) {
      existing = true;

      if (
        cleanString(
          existingSnapshot
            .data()
            ?.status
        ) ===
        "complete"
      ) {
        throw new Error(
          "This stock count has already been finalised and cannot be edited."
        );
      }
    }
  } else {
    countReference =
      doc(
        collection(
          db,
          "venues",
          venue,
          "stock_counts"
        )
      );
  }

  const header = {
    venue_id:
      venue,

    status:
      "draft",

    line_count:
      lines.length,

    delivery_note_ids:
      deliveryIds,

    internal_transfer_ids:
      transferIds,

    recipe_sales_entries:
      cleanedRecipeSalesEntries,

    saved_at:
      serverTimestamp(),

    saved_by_uid:
      user.uid,

    saved_by_email:
      user.email ||
      "",

    updated_at:
      serverTimestamp(),
  };

  if (!existing) {
    header.created_by_uid =
      user.uid;

    header.created_by_email =
      user.email ||
      "";

    header.created_at =
      serverTimestamp();
  }

  await setDoc(
    countReference,
    header,
    {
      merge: true,
    }
  );

  await replaceStockCountLines(
    countReference,
    lines
  );

  return (
    countReference.id
  );
}

export async function finaliseStockCount({
  venueId,
  countId,
}) {
  const venue =
    cleanString(
      venueId
    );

  const stockCountId =
    cleanString(
      countId
    );

  if (
    !venue ||
    !stockCountId
  ) {
    throw new Error(
      "Missing venue or stock count ID."
    );
  }

  const user =
    auth.currentUser;

  if (!user) {
    throw new Error(
      "You must be signed in to finalise a stock count."
    );
  }

  const countReference =
    doc(
      db,
      "venues",
      venue,
      "stock_counts",
      stockCountId
    );

  const countSnapshot =
    await getDoc(
      countReference
    );

  if (
    !countSnapshot.exists()
  ) {
    throw new Error(
      "The saved stock count could not be found."
    );
  }

  if (
    cleanString(
      countSnapshot
        .data()
        ?.status
    ) ===
    "complete"
  ) {
    throw new Error(
      "This stock count has already been finalised."
    );
  }

  const linesSnapshot =
    await getDocs(
      collection(
        countReference,
        "lines"
      )
    );

  if (
    linesSnapshot.empty
  ) {
    throw new Error(
      "The stock count contains no lines."
    );
  }

  const data =
    countSnapshot.data();

  const deliveryIds =
    uniqueStrings(
      data
        ?.delivery_note_ids
    );

  const transferIds =
    uniqueStrings(
      data
        ?.internal_transfer_ids
    );

  if (
    deliveryIds.length +
      transferIds.length >
    450
  ) {
    throw new Error(
      "Too many stock movements are attached to this count."
    );
  }

  const finalBatch =
    writeBatch(db);

  finalBatch.update(
    countReference,
    {
      status:
        "complete",

      completed_at:
        serverTimestamp(),

      finalised_at:
        serverTimestamp(),

      finalised_by_uid:
        user.uid,

      finalised_by_email:
        user.email ||
        "",

      updated_at:
        serverTimestamp(),
    }
  );

  for (
    const deliveryNoteId of
    deliveryIds
  ) {
    finalBatch.update(
      doc(
        db,
        "venues",
        venue,
        "stock_delivery_notes",
        deliveryNoteId
      ),
      {
        included_in_count_id:
          stockCountId,

        included_in_count_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),
      }
    );
  }

  for (
    const transferId of
    transferIds
  ) {
    finalBatch.update(
      doc(
        db,
        "venues",
        venue,
        "stock_internal_transfers",
        transferId
      ),
      {
        included_in_count_id:
          stockCountId,

        included_in_count_at:
          serverTimestamp(),

        updated_at:
          serverTimestamp(),
      }
    );
  }

  await finalBatch.commit();

  return stockCountId;
}

export async function saveStockCount({
  venueId,
  countId = "",
  lines,
  deliveryNoteIds = [],
  internalTransferIds = [],
  recipeSalesEntries = [],
}) {
  const savedCountId =
    await saveStockCountDraft({
      venueId,
      countId,
      lines,
      deliveryNoteIds,
      internalTransferIds,
      recipeSalesEntries,
    });

  await finaliseStockCount({
    venueId,
    countId:
      savedCountId,
  });

  return savedCountId;
}